import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  untracked,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import {
  Database,
  ref,
  objectVal,
  listVal,
  update,
  remove,
  push,
  set,
  get,
  serverTimestamp,
} from "@angular/fire/database";
import { AuthService } from "./auth.service";
import { StorageService } from "./storage.service";
import { FunctionsService } from "./functions.service";
import { TrackingService } from "./tracking.service";
import { UtilService } from "./util.service";
import { TranslateService } from "@ngx-translate/core";
import { defaults } from "../shared/defaults";
import { DEFAULT_CHIP_DESIGN_ID } from "../shared/chip-colors";
import { AppStatsService } from "./app-stats.service";
import { LocationService } from "./location.service";
import { ClubService } from "./club.service";
import { ConfigService } from "./config.service";
import { RoutingService } from "./routing.service";
import { readSignupContext } from "./signup-context";
import { ApiService } from "./api.service";
import { BvDialogService, BvToastService } from "../design-system";
import { AlertDialogComponent } from "../shared/alert-dialog/alert-dialog.component";
import { RotateService } from "./rotate.service";
import { environment } from "../../environments/environment";
import { CookieService } from "ngx-cookie-service";
import { of, switchMap, take, map } from "rxjs";

@Injectable({ providedIn: "root" })
export class ProfileService {
  private readonly mdb = inject(Database); // modular — signals + AF writes
  private readonly injector = inject(EnvironmentInjector);
  private readonly auth = inject(AuthService);
  private readonly storageServ = inject(StorageService);
  private readonly fbFunc = inject(FunctionsService);

  // --- Live profile data (modular AngularFire, keyed on auth.uidSignal) ---
  // Each stream re-keys when the signed-in uid changes and resets to null/[]
  // on logout, so the old per-login `auth.user.subscribe` cascade (and its
  // leaked subscriptions) is gone.
  private readonly profileSig = toSignal(
    toObservable(this.auth.uidSignal).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              objectVal<any>(ref(this.mdb, `profiles/${id}`))
            )
          : of(null)
      )
    ),
    { initialValue: null as any }
  );
  private readonly clubsSig = toSignal(
    toObservable(this.auth.uidSignal).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `profiles/${id}/clubs`))
            )
          : of([])
      )
    ),
    { initialValue: [] as any[] }
  );
  private readonly subscriptionSig = toSignal(
    toObservable(this.auth.uidSignal).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              objectVal<any>(ref(this.mdb, `profiles/${id}/subscription`))
            )
          : of(null)
      )
    ),
    { initialValue: null as any }
  );
  private readonly joinRequestsSig = toSignal(
    toObservable(this.auth.uidSignal).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `profiles/${id}/joinRequests`))
            )
          : of([])
      )
    ),
    { initialValue: [] as any[] }
  );
  // Templates carry their push-key as `key` (listVal keyField) — the old code
  // hand-injected it via snapshotChanges; consumers read `.key`/`.name`.
  private readonly templatesSig = toSignal(
    toObservable(this.auth.uidSignal).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `templates/${id}`), { keyField: "key" })
            )
          : of([])
      )
    ),
    { initialValue: [] as any[] }
  );

  // Optimistic override for latestSubscription (post-checkout instant render);
  // cleared automatically when the realtime listener pushes the next value.
  private readonly subOverride = signal<any>(undefined);

  // Signal-backed UI flags (zoneless-ready). Exposed as get/set so the many
  // existing property-style call sites are unchanged: subscription.service
  // assigns sub_processing; AppComponent binds [open]="profServ.tcDialogOpen".
  private readonly _subProcessing = signal<boolean | null>(false);
  private readonly _tcDialogOpen = signal(false);

  // --- Derived state (computed; fixes the old recompute-in-subscribe staleness) ---
  private readonly disableDeleteSig = computed(() => {
    const p = this.profileSig();
    return p && p.clubs ? Object.keys(p.clubs).length < 2 : true;
  });
  // Preserves the original (odd) `!(len < 1)` form verbatim — flagged for a
  // readability follow-up (it's just `len >= 1`).
  private readonly noClubsSig = computed(() => {
    const p = this.profileSig();
    return p && p.clubs ? !(Object.keys(p.clubs).length < 1) : true;
  });

  // --- Legacy field getters (consumers unchanged) ---
  get latestProfile() {
    return this.profileSig();
  }
  get latestClubs() {
    return this.clubsSig();
  }
  get latestSubscription() {
    const ov = this.subOverride();
    return ov !== undefined ? ov : this.subscriptionSig();
  }
  set latestSubscription(sub: any) {
    // Optimistic post-checkout render — see subscription.service.applyOptimisticSub.
    this.subOverride.set(sub);
  }
  get latestTemplates() {
    return this.templatesSig();
  }
  get latestJoinRequests() {
    return this.joinRequestsSig();
  }
  get tournaments() {
    return this.profileSig()?.tournaments;
  }
  get uid(): string | null {
    return this.auth.uid;
  }
  get disableDelete() {
    return this.disableDeleteSig();
  }
  get noClubs() {
    return this.noClubsSig();
  }
  get sub_processing(): boolean | null {
    return this._subProcessing();
  }
  set sub_processing(v: boolean | null) {
    this._subProcessing.set(v);
  }
  // Drives the [open] binding of <app-tc-acceptance-dialog> mounted in
  // AppComponent. Flipped to true on first profile load when acceptance is
  // missing/outdated; reset on accept, logout, or sign-out.
  get tcDialogOpen(): boolean {
    return this._tcDialogOpen();
  }
  set tcDialogOpen(v: boolean) {
    this._tcDialogOpen.set(v);
  }

  // --- Modular write handles (replace the old AngularFireObject/List) ---
  // Each op runs inside the injection context — AngularFire's modular database
  // functions call inject(NgZone) internally and warn/misbehave otherwise.
  get profileAF() {
    const id = this.auth.uid;
    return {
      update: (data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `profiles/${id}`), data)
        ),
      remove: () =>
        runInInjectionContext(this.injector, () =>
          remove(ref(this.mdb, `profiles/${id}`))
        ),
    };
  }
  private get subscriptionAF() {
    const id = this.auth.uid;
    return {
      update: (data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `profiles/${id}/subscription`), data)
        ),
    };
  }
  get templatesAF() {
    const id = this.auth.uid;
    return {
      update: (key: string, data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `templates/${id}/${key}`), data)
        ),
      remove: (key: string) =>
        runInInjectionContext(this.injector, () =>
          remove(ref(this.mdb, `templates/${id}/${key}`))
        ),
      push: (data: any) =>
        runInInjectionContext(this.injector, () =>
          push(ref(this.mdb, `templates/${id}`), data)
        ),
    };
  }

  // --- Root-level modular write/read helpers (replace the old `rootRef`) ---
  // Multi-path fan-out updates write at the database root with slash-keyed
  // paths; single-node ops take an explicit path. All wrapped in the injection
  // context (AngularFire zone-stability requirement).
  private rootUpdate(updates: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb), updates)
    );
  }
  private nodeSet(path: string, data: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, path), data)
    );
  }
  private nodeGet(path: string): Promise<any> {
    return runInInjectionContext(this.injector, () => get(ref(this.mdb, path)));
  }

  // --- Plain mutable state (cross-component scratch + UI flags; unchanged) ---
  clubLoaded: boolean = false;
  clubCreated: boolean = false;
  subCurrency!: string;
  subInterval!: string;
  planIndex!: number;
  amountDue!: number;
  private _loadingApproveJoin = signal(false);
  get loadingApproveJoin() { return this._loadingApproveJoin(); }
  set loadingApproveJoin(v: boolean) { this._loadingApproveJoin.set(v); }
  signupPlanSelection: {
    plan?: string;
    interval?: string;
    currency?: string;
    planIndex?: number;
    shouldOpenCheckout?: boolean;
  } = {};

  // Per-uid guards replacing the old needProfileInit/needClubsInit flags so the
  // one-time effects stay idempotent as the profile signal re-emits.
  private bootstrappedUids = new Set<string>();
  private clubsSelected = new Set<string>();
  private chipsetCheckedUids = new Set<string>();

  constructor(
    private tracking: TrackingService,
    private util: UtilService,
    private routing: RoutingService,
    private appStats: AppStatsService,
    private dialog: BvDialogService,
    private translate: TranslateService,
    public loc: LocationService,
    public clubServ: ClubService,
    private configServ: ConfigService,
    private snackBar: BvToastService,
    private rotateServ: RotateService,
    private api: ApiService,
    private cookieService: CookieService
  ) {
    // Clear the optimistic subscription override whenever the realtime listener
    // pushes a fresh value (the server already wrote the same object).
    effect(() => {
      this.subscriptionSig();
      this.subOverride.set(undefined);
    });

    // Apply the user's saved UI language when it changes.
    effect(() => {
      const prof = this.profileSig();
      if (prof) this.checkLanguage(prof);
    });

    // Drive date/number formatting from the user's region (independent of UI
    // language, mirroring the OS Language-vs-Region split). The locale is
    // `<language>-<region>` (e.g. en-FR → English words, DD/MM/YYYY ordering).
    // Resolution order:
    //   1. The region the user explicitly picked in their profile.
    //   2. Else their geo-detected country (locationData.countryCode, written
    //      server-side by geoLoc) — so existing en-* profiles format by where
    //      the user actually is, not by their language (which defaulted to US).
    //   3. Else UtilService's platform default (Intl region-aware locale).
    effect(() => {
      const prof = this.profileSig();
      const lang = (prof?.selLang || this.translate.currentLang || "en").split("-")[0];
      const region = (prof?.region || prof?.locationData?.countryCode || "").toUpperCase();
      this.util.setDateLocale(region ? `${lang}-${region}` : null);
    });

    // Select the active club once per uid when clubs first appear; otherwise
    // show the no-club state. (Was the needClubsInit branch.)
    effect(() => {
      const uid = this.auth.uidSignal();
      const prof = this.profileSig();
      if (!uid) return;
      if (prof && prof.clubs) {
        if (!this.clubsSelected.has(uid)) {
          this.clubsSelected.add(uid);
          this.clubCreated = true;
          this.loadClub(prof);
        }
      } else if (prof) {
        this.clubServ.noClub();
      }
    });

    // Reconcile the subscription level/limit down to the profile (+ owner clubs)
    // whenever the subscription or profile changes. (Was initSubscriptions.)
    // NOTE: reads-trigger-writes — flagged to shrink to profile-only and move
    // the club fan-out into the Stripe-webhook CF in a later commit.
    effect(() => {
      const prof = this.profileSig();
      const results = this.subscriptionSig();
      if (!prof || !prof.uid) return;
      if (results) {
        if (
          results.metadata &&
          results.metadata.level &&
          !results.metadata.player_limit
        ) {
          this.subscriptionAF.update({
            "metadata/player_limit": this.configServ.getPlayerLimit(
              results.code
            ),
          });
        } else if (
          results.metadata &&
          (results.metadata.level != prof.subLevel ||
            results.metadata.player_limit != prof.player_limit)
        ) {
          this.updateSubLevel(
            results.metadata.level,
            results.metadata.player_limit
          );
          // Don't sync sub.plan.currency → profile.currency. profile.currency
          // is the geo currency (what the user pays for new charges);
          // sub.plan.currency is the Stripe-pinned currency for the existing
          // sub. They diverge in cross-currency upgrades — the conversion flow
          // handles that.
        }
      } else if (prof.subLevel != 0) {
        this.updateSubLevel(0, 9);
      }
    });

    // Surface the T&C/Privacy acceptance dialog on profile load when due.
    // untracked: maybeShow reads+writes the tcDialogOpen signal, which would
    // otherwise make this effect depend on (and loop on) its own write.
    effect(() => {
      const prof = this.profileSig();
      untracked(() => this.maybeShowTcAcceptanceDialog(prof));
    });

    // Profile↔CF JWT handshake: once the CF mints the jwt, clear our createJWT
    // request flag (the client owns "I've consumed the JWT").
    effect(() => {
      const prof = this.profileSig();
      if (prof && prof.jwt && prof.createJWT) {
        this.profileAF.update({ createJWT: null });
      }
    });

    // One-time legacy chipset migration, once per uid. (Placement unchanged for
    // now; flagged to fold into the bootstrap + a server-side backfill.)
    effect(() => {
      const uid = this.auth.uidSignal();
      if (uid && !this.chipsetCheckedUids.has(uid)) {
        this.chipsetCheckedUids.add(uid);
        this.checkOldChipsets(uid);
      }
    });

    // Reset per-session UI flags + guards on logout. (Data signals reset to
    // null/[] automatically via the switchMap → of(null/[]) on uid===null.)
    effect(() => {
      if (!this.auth.uidSignal()) {
        this.clubCreated = false;
        this.clubLoaded = false;
        this.sub_processing = false;
        this.tcDialogOpen = false;
        this.bootstrappedUids.clear();
        this.clubsSelected.clear();
        this.chipsetCheckedUids.clear();
        this.clubServ.noClub();
      }
    });

    // One-time login / new-user bootstrap. Runs on the FIRST emission of the
    // profile object after login — objectVal's first value is the settled DB
    // state, which is exactly how the old code discriminated new-vs-returning
    // users (an empty profile means "new"). take(1) + the per-uid guard keep it
    // idempotent; the inner stream auto-completes so there's nothing to leak.
    toObservable(this.auth.uidSignal)
      .pipe(
        switchMap((id) =>
          id
            ? runInInjectionContext(this.injector, () =>
                objectVal<any>(ref(this.mdb, `profiles/${id}`))
              ).pipe(
                take(1),
                map((prof) => ({ id, prof }))
              )
            : of(null)
        )
      )
      .subscribe((res) => {
        if (!res) return;
        const { id, prof } = res;
        if (this.bootstrappedUids.has(id)) return;
        this.bootstrappedUids.add(id);
        const user = this.auth.authUser();
        if (user) this.initProfile(prof || {}, user);
      });
  }
  /**
   * If the user hasn't accepted the current T&C/Privacy version, set the
   * tcDialogOpen flag. AppComponent's <app-tc-acceptance-dialog> binds to
   * this flag and presents the bv-dialog. The accepted/loggedOut events
   * route back to onTcAccepted / onTcLogout below.
   */
  private maybeShowTcAcceptanceDialog(profileData: any) {
    if (!profileData || !profileData.uid) return;
    const accepted = profileData.tcAcceptedVersion;
    if (accepted && accepted >= environment.tcVersion) {
      // Already accepted the current version — make sure the dialog is closed.
      // An earlier partial profile emit (uid present, tcAcceptedVersion not yet
      // loaded) can open the dialog; the later complete emit must be able to
      // dismiss it, so check acceptance BEFORE the already-open short-circuit.
      this.tcDialogOpen = false;
      return;
    }
    if (this.tcDialogOpen) return;
    this.tcDialogOpen = true;
  }

  onTcAccepted(): void {
    this.tcDialogOpen = false;
    this.profileAF
      .update({
        tcAcceptedVersion: environment.tcVersion,
        tcAcceptedAt: Date.now(),
      })
      .catch((e) => console.warn("[tc] failed to record acceptance", e));
  }

  onTcLogout(): void {
    this.tcDialogOpen = false;
    this.auth.logout();
  }
  // disableDelete is now a computed getter; this is retained as a no-op so the
  // existing call site (club.component) keeps compiling.
  disableDeleteClub() {}
  // Session-state fields (lastClub, lobbyView, lobbyFilter, lobbySort) live in
  // sessionStorage (per-tab) + localStorage (per-device) so that simultaneous
  // sessions on different devices/tabs don't race-clobber each other via a
  // shared Firebase field. The Firebase mirror is kept only as a "new device"
  // bootstrap — local storage takes precedence on every read.
  getLocalPref(field: string): string | null {
    if (!this.uid) return null;
    const key = `${field}:${this.uid}`;
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  private setLocalPref(field: string, value: string) {
    if (!this.uid) return;
    const key = `${field}:${this.uid}`;
    try {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    } catch (e) {
      // Storage can throw in private mode / quota exceeded; ignore.
    }
  }
  setLastClub(clubID: string) {
    this.setLocalPref("selectedClub", clubID);
    if (this.latestProfile && this.latestProfile.lastClub != clubID)
      this.profileAF.update({ lastClub: clubID });
  }
  setLobbyView(mode: string) {
    this.setLocalPref("lobbyView", mode);
    if (this.latestProfile?.lobbyView != mode)
      this.profileAF.update({ lobbyView: mode });
  }
  setLobbyFilters(status: string, sort: string) {
    this.setLocalPref("lobbyFilter", status);
    this.setLocalPref("lobbySort", sort);
    const update: any = {};
    if (this.latestProfile?.lobbyFilter != status) update.lobbyFilter = status;
    if (this.latestProfile?.lobbySort != sort) update.lobbySort = sort;
    if (Object.keys(update).length) this.profileAF.update(update);
  }
  setShowStaleRunning(show: boolean) {
    this.setLocalPref("showStaleRunning", show ? "1" : "0");
  }
  // refuseNewsletter is the canonical field — string "true" means refused, anything
  // else (including unset on legacy profiles) is treated as opted-in. Set during
  // signup from the marketing-page checkbox; the Preferences tab toggle is the
  // only in-app way to flip it afterward.
  get newsletterAccepted(): boolean {
    return this.latestProfile?.refuseNewsletter !== "true";
  }
  setNewsletterAccepted(accept: boolean): void {
    const value = accept ? "false" : "true";
    this.profileAF.update({ refuseNewsletter: value });
  }
  private updateSubLevel(level: number, player_limit: number) {
    // Profile-only. The old code also fanned the level/limit out to every
    // owner club here (a read-triggered client write); ClubService's
    // updateClubLevel effect already reconciles each club against the owner's
    // plan when the club loads, so the fan-out is redundant. The durable fix
    // is for the Stripe-webhook CF to fan this out server-side on plan change.
    this.profileAF.update({
      subLevel: level,
      player_limit: player_limit,
    });
  }
  private loadClub(profileData: any) {
    // Resolution order: per-tab → per-device → Firebase profile.lastClub
    // (new-device fallback) → first available club.
    const stored = this.getLocalPref("selectedClub");
    let clubID = stored || profileData.lastClub;
    // A stored/lastClub ID can go stale (membership removed, club quit from
    // another device, club deleted) — the club's reads would then be denied
    // and the whole club UI renders blank. Only trust an ID the profile still
    // has a membership for; otherwise fall through to the first valid club.
    if (clubID && !profileData.clubs?.[clubID]) clubID = null;
    if (clubID) {
      this.clubServ.init(clubID);
      // Seed local storage on first login from this device so subsequent
      // tabs/loads stay on the user's last selection without re-consulting
      // Firebase (which can be racy across simultaneous sessions).
      if (!stored) this.setLastClub(clubID);
    } else {
      this.loadFirstClub(profileData);
    }
  }
  public loadFirstClub(profileData?: any) {
    let prof: any;
    if (profileData) prof = profileData;
    else prof = this.latestProfile;
    if (prof && prof.clubs) {
      for (let c in prof.clubs) {
        if (prof.clubs[c].clubID != this.clubServ.deletedClub) {
          this.clubServ.init(prof.clubs[c].clubID);
          this.setLastClub(prof.clubs[c].clubID);
          break;
        }
      }
    } else this.clubServ.noClub();
  }
  private checkLanguage(profileData: any) {
    if (
      profileData.selLang &&
      profileData.selLang != this.translate.currentLang
    ) {
      //this.translate.currentLang = profileData.selLang
      this.translate.use(profileData.selLang);
      this.translate.reloadLang(profileData.selLang);
    }
  }
  private initProfile(profileData: any, userData: any) {
    let sendInBlue = {
      email: "",
      name: "",
      lang: "",
      country: "",
    };
    const tmpProvider = this.auth.getProvider(userData);
    const trackParams = {
      name: this.getDisplayName(userData, tmpProvider),
      uid: userData.uid,
      method: profileData.provider,
    };
    let update: any = {};
    let newUser!: boolean;
    if (!profileData.uid) {
      newUser = true;
      update.uid = userData.uid;
      update.currency = "USD";
      update.subInterval = "monthly";
      update.refuseNewsletter = this.cookieService.get("refuseNewsletter");
      if (update.refuseNewsletter === "false") {
        this.api.geoLoc().then((res) => {
          sendInBlue.email = userData.providerData[0].email;
          sendInBlue.name = this.getDisplayName(userData, tmpProvider);
          sendInBlue.lang = this.translate.getBrowserLang()!;
          sendInBlue.country = res.data.country;
          this.api.createContactSIB(sendInBlue);
        });
      }
      if (this.subCurrency) update.currency = this.subCurrency;
      if (this.subInterval) update.subInterval = this.subInterval;
      this.appStats.signup();
      if (tmpProvider == "password") {
        this.auth.sendEmailVerification();
        update.verificationSent = true;
      }
      this.tracking.track("sign_up", trackParams);
    } else {
      if (!profileData.currency) {
        update.currency = "USD";
        if (this.subCurrency) update.currency = this.subCurrency;
      }
      if (!profileData.subInterval) {
        update.subInterval = "monthly";
        if (this.subInterval) update.subInterval = this.subInterval;
      }
      this.tracking.track("login", trackParams);
    }

    if (!profileData.provider) update.provider = tmpProvider;
    if (!profileData.dispName)
      update.dispName = this.getDisplayName(userData, update.provider);
    if (
      !profileData.imgURL ||
      profileData.imgType == update.provider ||
      profileData.imgURL == "/images/anonymous.jpg" ||
      profileData.imgURL == "/assets/img/anonymous.png"
    )
      update.imgURL = this.getImgURL(userData);
    // Auto-create "Club <name>" only for a brand-new profile that did not
    // arrive through a club/tournament link (signup context). Existing
    // profiles with no clubs get the lobby's no-club state instead of a
    // surprise club on every login.
    if (newUser && !this.clubCreated && !readSignupContext()) {
      this.clubCreated = true;
      this.clubServ.quickClub(
        update.dispName || profileData.dispName,
        profileData.subLevel || 0,
        profileData.player_limit || 9,
        update.imgURL || profileData.imgURL || "/assets/img/anonymous.png"
      );
    }
    if (!profileData.email) update.email = userData.providerData[0].email;
    if (!profileData.createDate) update.createDate = serverTimestamp();
    update.lastLogin = serverTimestamp();
    update.sortLogin = 0 - Date.now();
    if (profileData.connections)
      update.connections = profileData.connections + 1;
    else update.connections = 1;
    if (!profileData.selLang) {
      update.detectedLang = this.translate.getBrowserLang();
      update.selLang = update.detectedLang;
    }
    if (!profileData.chipsets) update.chipsets = defaults.chipsets;
    if (!profileData.chipColors) update.chipColors = defaults.chipColors;
    if (!profileData.chipDesigns) update.chipDesigns = defaults.chipDesigns;
    if (!profileData.defaults) update.defaults = defaults.tournDefaults;
    if (
      tmpProvider == "password" &&
      userData.emailVerified == false &&
      profileData.verificationSent
    )
      update.prevVerified = true;
    this.updateProfile(update);
    if (!profileData.locationData || !profileData.locationData.city)
      this.loc.updateLocationData(userData.uid);
    else this.rotateServ.init(profileData.locationData.country);
    // One-time currency migration: legacy users have profile.currency set
    // by the old "EU=EUR/else=USD" heuristic or by the now-removed
    // sub.plan.currency auto-sync. Both are wrong post-pricing-restructure
    // (we want the geo currency for display + upgrade path). On the user's
    // first login after this cutoff, refresh from the catalog. Subsequent
    // logins are no-ops (lastLogin is updated to now during initProfile).
    const CURRENCY_REFRESH_CUTOFF = Date.parse("2026-05-04T23:59:59+02:00");
    const needsCurrencyRefresh =
      !profileData.currency ||
      (profileData.lastLogin &&
        profileData.lastLogin < CURRENCY_REFRESH_CUTOFF);
    if (
      profileData.locationData &&
      (profileData.locationData.countryCode ||
        profileData.locationData.country) &&
      needsCurrencyRefresh
    )
      this.loc.updateTVA(
        profileData.locationData.countryCode ||
          profileData.locationData.country,
        userData.uid
      );
    if (!profileData.jwt) {
      this.createJWT();
    }
    if (profileData.stripeID)
      this.api.setStripeCards(profileData.uid, profileData.stripeID);
    if (newUser) {
      setTimeout(() => {
        this.api.sendEvent("EVT_NEW_USER", this.latestProfile);
      }, 4000);
    }
  }
  createJWT() {
    this.profileAF.update({
      createJWT: true,
    });
  }
  private updateProfile(update: any) {
    this.profileAF.update(update);
  }

  private getDisplayName(userData: any, provider: string) {
    if (provider == "password")
      return this.util.extractName(userData.providerData[0].email);
    else return userData.providerData[0].displayName || "";
  }
  updateUser(
    newName: string,
    imageUrl: string,
    imageType: string,
    language: string,
    firstName?: string,
    lastName?: string,
    region?: string
  ) {
    let profileUpdates: any = {};
    let uid = this.uid;
    let first = firstName ? firstName.trim() : null;
    let last = lastName ? lastName.trim() : null;
    profileUpdates[`profiles/${uid}/imgURL`] = imageUrl;
    profileUpdates[`profiles/${uid}/imgType`] = imageType;
    profileUpdates[`profiles/${uid}/dispName`] = newName;
    profileUpdates[`profiles/${uid}/firstName`] = first;
    profileUpdates[`profiles/${uid}/lastName`] = last;
    profileUpdates[`profiles/${uid}/selLang`] = language;
    // Empty string = "Auto (detected)" → store null so the region clears.
    profileUpdates[`profiles/${uid}/region`] = region ? region : null;
    for (var club in this.latestProfile.clubs) {
      profileUpdates[`clubs/${club}/members/${uid}/memberName`] = newName.trim();
      profileUpdates[`clubs/${club}/members/${uid}/sortName`] =
        newName.trim().toLowerCase();
      profileUpdates[`clubs/${club}/members/${uid}/imgURL`] = imageUrl;
      profileUpdates[`clubs/${club}/members/${uid}/firstName`] = first;
      profileUpdates[`clubs/${club}/members/${uid}/lastName`] = last;
    }
    // No direct tournament dispName writes here anymore: each member-record
    // write above fires the onMemberNameChange trigger, which repairs ALL
    // name copies in that club's tournaments (players incl. re-entry bullets,
    // seats, killer names, winnerName — the old loop only patched the primary
    // player record). Tournaments in clubs the user has since left keep their
    // historical name, matching how deleted members are handled.
    this.rootUpdate(profileUpdates);
  }
  private getImgURL(userData: any) {
    if (userData.providerData[0].photoURL)
      return userData.providerData[0].photoURL;
    else return defaults.profileImage;
  }

  updateEmail(email: string) {
    let profile: any = {};
    let uid = this.uid;
    profile[`profiles/${uid}/email`] = email;
    this.rootUpdate(profile);
  }
  async convertChipsets(old_chipsets: string[] | { [key: string]: string }) {
    const removeLeadingAndTrailingCommas = (chipsetString: string) => {
      return chipsetString.replace(/^,+|,+$/g, "");
    };

    const tmp_chipsets = Array.isArray(old_chipsets)
      ? old_chipsets
          .map((chipsetString) =>
            removeLeadingAndTrailingCommas(chipsetString)
              .split(",")
              .filter((chip) => chip !== "")
              .map((chip) => parseFloat(chip))
          )
          .filter((chipArray) => chipArray.length > 0)
      : Object.values(old_chipsets)
          .map((chipsetString) =>
            removeLeadingAndTrailingCommas(chipsetString)
              .split(",")
              .filter((chip) => chip !== "")
              .map((chip) => parseFloat(chip))
          )
          .filter((chipArray) => chipArray.length > 0);

    await this.nodeSet(`profiles/${this.uid}/chipsets`, tmp_chipsets);
  }

  isAdmin() {
    const p = this.profileSig();
    return p && p.uid ? this.configServ.isAdmin(p.uid) : false;
  }
  getProfileForUserData(uid: string, tournID: string) {
    let path = "profiles/" + uid;
    return runInInjectionContext(this.injector, () =>
      objectVal<any>(ref(this.mdb, path))
    );
  }
  getImageUrlForConnectedUser() {
    return this.auth.getUserConnected().imageUrl;
  }
  uploadProfileImage(
    img64: string,
    callback: (imgURL: string, imgType: string) => void,
    failure: (error: any) => void
  ) {
    this.storageServ
      .uploadDataUrl(`${this.uid}/profileImage`, img64)
      .then((url) => {
        callback(url, "custom");
      })
      .catch((error) => {
        failure(error);
      });
  }
  deleteProfilePicture(): Promise<any> {
    const deleteFunc = this.fbFunc.httpsCallable("deleteProfilePicture");
    return deleteFunc().then((result) => {
      return result.data;
    }).catch((error) => {
      console.error("Error deleting profile picture:", error);
      throw error;
    });
  }
  approveJoin(request: any) {
    this.loadingApproveJoin = true;
    // The server clears the request in the same write as the join and
    // notifies the requester.
    this.joinClub(this.clubServ.clubID, request);
  }
  joinClub(clubID: string, profile: any, inviteID?: string) {
    let joinProm: Promise<string> = this.api.joinClub(
      clubID,
      profile,
      inviteID
    );
    return joinProm.then((result: any) => {
      if (result.data == "JOINED_CLUB")
        this.translate.get(["JOINED_CLUB", "CLOSE"]).subscribe((res: any) => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              message: res["JOINED_CLUB"],
              title: clubID,
              closeButton: res["CLOSE"],
            },
            panelClass: "alert-dialog-container",
          });
        });
      else if (result.data == "REQUEST_APPROVED")
        this.translate
          .get(["REQUEST_APPROVED"])
          .subscribe((res: any) => {
            this.snackBar.open(
              res["REQUEST_APPROVED"] + ": " + profile.dispName,
              undefined,
              { duration: 4000 }
            );
          });
      else if (result.data == "CLUB_NOT_FOUND")
        this.translate
          .get(["CLUB_NOT_FOUND", "CLOSE"])
          .subscribe((res: any) => {
            this.dialog.open(AlertDialogComponent, {
              data: {
                message: clubID,
                title: res["CLUB_NOT_FOUND"],
                closeButton: res["CLOSE"],
              },
              panelClass: "alert-dialog-container",
            });
          });
      else if (typeof result.data == "string")
        // ALREADY_MEMBER, INVALID_INVITE, FORBIDDEN, REQUEST_NOT_FOUND, ...
        this.translate.get([result.data]).subscribe((res: any) => {
          this.snackBar.open(res[result.data], undefined, { duration: 4000 });
        });
      this.loadingApproveJoin = false;
      return result.data;
    });
  }

  // 4-char code dialog. Server-side: joins instantly when the club is open,
  // else files the request (no client-side grants write any more).
  createJoinRequest(club: any): Promise<string> {
    return this.api
      .requestJoinClub(club.clubID, "code")
      .then((result: any) => {
        const code: string = result?.data || "GENERIC_ERROR";
        if (code == "JOINED_CLUB") {
          this.clubServ.init(club.clubID);
          this.setLastClub(club.clubID);
        }
        const withName = code == "JOINED_CLUB" || code == "REQUEST_SENT";
        this.translate.get([code]).subscribe((res: any) => {
          this.snackBar.open(
            withName ? res[code] + ": " + club.clubName : res[code],
            undefined,
            { duration: 4000 }
          );
        });
        return code;
      });
  }
  // Club link (/join/{clubID}). The page renders the result itself.
  joinViaLink(clubID: string): Promise<string> {
    return this.api
      .requestJoinClub(clubID, "link")
      .then((result: any) => result?.data || "GENERIC_ERROR");
  }
  changeInterval(interval: string) {
    this.profileAF.update({ subInterval: interval });
  }
  updateChipsets(chipsets: any, chipColors?: any, chipDesigns?: any) {
    // Drop falsy denoms within each slot, then drop empty outer slots from
    // chipsets/chipColors/chipDesigns in lockstep. Empty inner arrays and
    // all-null arrays are stored as "no data" by Firebase RTDB, which on
    // re-read produces sparse arrays / object-form nodes — see chipColors
    // becoming `{ "3": [...] }` once slots 0/1/2 had no painted colors.
    const cleanSets: number[][] = [];
    const cleanCols: any[][] = [];
    const cleanDes: string[] = [];
    const hasColors = chipColors !== undefined;
    const hasDesigns = chipDesigns !== undefined;
    for (let i = 0; i < chipsets.length; i++) {
      const oldDenoms: any[] = chipsets[i] || [];
      const oldColors: any[] = (chipColors && chipColors[i]) || [];
      const newDenoms: number[] = [];
      const newColors: any[] = [];
      for (let j = 0; j < oldDenoms.length; j++) {
        const v = oldDenoms[j];
        if (!v) continue;
        newDenoms.push(parseFloat(v));
        newColors.push(oldColors[j] && oldColors[j].p ? oldColors[j] : null);
      }
      if (newDenoms.length === 0) continue;
      cleanSets.push(newDenoms);
      if (hasColors) cleanCols.push(newColors);
      if (hasDesigns)
        cleanDes.push(chipDesigns[i] || DEFAULT_CHIP_DESIGN_ID);
    }
    const update: any = { chipsets: cleanSets };
    if (hasColors) update.chipColors = cleanCols;
    if (hasDesigns) update.chipDesigns = cleanDes;
    this.profileAF.update(update);
  }
  checkOldChipsets(uid: string) {
    this.nodeGet(`profiles/${uid}/chipsets`).then((snapshot) => {
      const chipsets = snapshot.val();
      if (
        chipsets &&
        (!Array.isArray(chipsets) || !Array.isArray(chipsets[0]))
      ) {
        this.convertChipsets(chipsets);
      }
    });
    this.nodeGet(`profiles/${uid}/defaults/chipset`).then((snapshot) => {
      const chipset = snapshot.val();
      if (chipset && !Array.isArray(chipset)) {
        const chipsetArr = chipset
          .split(",")
          .map((chip: string) => parseFloat(chip));
        this.nodeSet(`profiles/${uid}/defaults/chipset`, chipsetArr);
      }
    });
  }

  setSignupPlanSelection(
    plan: string,
    interval: string,
    currency: string,
    planIndex: number
  ) {
    this.signupPlanSelection = {
      plan,
      interval,
      currency,
      planIndex,
      shouldOpenCheckout: true,
    };

    // Also set the existing properties for backward compatibility
    this.subCurrency = currency;
    this.subInterval = interval;
    this.planIndex = planIndex;
  }

  getSignupPlanSelection() {
    return this.signupPlanSelection;
  }

  clearSignupPlanSelection() {
    this.signupPlanSelection = {};
  }

  hasSignupPlanSelection(): boolean {
    return !!(
      this.signupPlanSelection.plan &&
      this.signupPlanSelection.interval &&
      this.signupPlanSelection.currency
    );
  }
  deleteTemplate(key: string) {
    this.templatesAF.remove(key);
  }
  renameTemplate(key: string, newName: string) {
    this.templatesAF.update(key, { name: newName });
  }
}
